import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Bell, MessageSquare, User, Wallet, CreditCard, FileText, 
  AlertTriangle, CheckCircle, Plus, Settings, Clock, Shield,
  Lock, Users, Zap, HelpCircle
} from 'lucide-react';
import { InboxDropdown } from './InboxDropdown';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useNotifications } from '@/hooks/useNotifications';
import { useMessages } from '@/hooks/useMessages';
import { useReminders } from '@/hooks/useReminders';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { WorkspaceSwitcher } from '@/components/WorkspaceSwitcher';

export const SmartHeaderWithCredits: React.FC = () => {
  const workspace = useWorkspace();
  // Safely handle workspace context - may not be available on all routes
  const currentWorkspace = workspace?.currentWorkspace || null;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCreditsDropdown, setShowCreditsDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  // Inbox hooks - gracefully handle when workspace is null
  const notifications = useNotifications();
  const messages = useMessages();
  const reminders = useReminders();

  // Feature flags - default to true when workspace not available (graceful degradation)
  const smartHeader = useFeatureFlag(currentWorkspace?.id, 'smart_header');
  const inboxNotifications = useFeatureFlag(currentWorkspace?.id, 'inbox_notifications');
  const inboxMessages = useFeatureFlag(currentWorkspace?.id, 'inbox_messages');
  const inboxReminders = useFeatureFlag(currentWorkspace?.id, 'inbox_reminders');
  const profileMenuFull = useFeatureFlag(currentWorkspace?.id, 'profile_menu_full');

  // Get workspace credit balance - only if workspace context is available
  const { data: creditWallet, isLoading: creditsLoading } = useQuery({
    queryKey: ['workspace-credits', currentWorkspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workspace_credit_wallets')
        .select('*')
        .eq('workspace_id', currentWorkspace?.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id,
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Get recent credit transactions
  const { data: recentTransactions } = useQuery({
    queryKey: ['credit-transactions', currentWorkspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('workspace_id', currentWorkspace?.id)
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id
  });

  // Get notifications count
  const { data: notificationsCount } = useQuery({
    queryKey: ['notifications-count', currentWorkspace?.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('billing_notifications')
        .select('*', { count: 'exact', head: true })
        .eq('workspace_id', currentWorkspace?.id)
        .eq('status', 'pending');
      
      if (error) throw error;
      return count || 0;
    },
    enabled: !!currentWorkspace?.id
  });

  const getCreditsStatus = () => {
    if (!creditWallet) return { color: 'bg-muted text-muted-foreground', level: 'unknown' };
    
    const balance = creditWallet.credits_balance;
    const critical = creditWallet.critical_balance_threshold;
    const low = creditWallet.low_balance_threshold;
    
    if (balance <= critical) {
      return { color: 'bg-destructive/10 text-destructive', level: 'critical', icon: AlertTriangle };
    } else if (balance <= low) {
      return { color: 'bg-warning/10 text-warning', level: 'low', icon: AlertTriangle };
    } else {
      return { color: 'bg-success/10 text-success', level: 'good', icon: CheckCircle };
    }
  };

  const creditsStatus = getCreditsStatus();

  return (
    <header className="bg-background border-b border-border px-4 py-3 sticky top-0 z-50 shadow-sm">
      <div className="flex items-center justify-between">
        {/* Left section: Logo and Context */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-primary-foreground font-bold text-sm">BP</span>
            </div>
            <div className="hidden md:block">
              <h1 className="text-xl font-bold text-foreground">BillPortal</h1>
            </div>
          </div>
        </div>
          
        {/* Right section: Workspace Switcher, Credits, Notifications, Profile */}
        <div className="flex items-center space-x-2">
          {/* Workspace Switcher */}
          {currentWorkspace && (
            <WorkspaceSwitcher />
          )}

          {/* Credits Balance - Only show if workspace context is available */}
          {currentWorkspace && (
            <div className="relative">
              <Button
                variant="ghost"
                onClick={() => setShowCreditsDropdown(!showCreditsDropdown)}
                className="flex items-center space-x-2 px-3 py-2 hover:bg-accent"
              >
                <Wallet className="h-4 w-4 text-muted-foreground" />
                {creditsLoading ? (
                  <div className="w-16 h-5 bg-muted animate-pulse rounded"></div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Badge className={`${creditsStatus.color} text-xs font-medium px-2 py-1`}>
                      {Math.floor(creditWallet?.credits_balance || 0)} credits
                    </Badge>
                    {creditsStatus.level !== 'good' && (
                      <creditsStatus.icon className="h-4 w-4 text-warning" />
                    )}
                  </div>
                )}
              </Button>

              {/* Credits Dropdown */}
              {showCreditsDropdown && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-background border border-border rounded-lg shadow-lg z-50">
                  <div className="p-4 border-b border-border">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-foreground">Credit Balance</h3>
                      <Badge className={creditsStatus.color}>
                        {creditsStatus.level.toUpperCase()}
                      </Badge>
                    </div>
                    <div className="mt-2">
                      <p className="text-2xl font-bold text-foreground">
                        {Math.floor(creditWallet?.credits_balance || 0)} credits
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Available for bill processing and automation
                      </p>
                    </div>
                  </div>

                  <div className="p-4">
                    {/* Quick Actions */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          navigate(`/w/${currentWorkspace?.id}/dashboard/billing?action=topup`);
                          setShowCreditsDropdown(false);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Top Up
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          navigate(`/w/${currentWorkspace?.id}/dashboard/billing`);
                          setShowCreditsDropdown(false);
                        }}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        View Bills
                      </Button>
                    </div>

                    {/* Recent Transactions */}
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-2">Recent Activity</h4>
                      <div className="space-y-2">
                        {recentTransactions?.slice(0, 3).map((transaction) => (
                          <div key={transaction.id} className="flex items-center justify-between text-sm">
                            <div className="flex items-center space-x-2">
                              {transaction.amount > 0 ? (
                                <Plus className="h-3 w-3 text-success" />
                              ) : (
                                <span className="h-3 w-3 bg-destructive rounded-full"></span>
                              )}
                              <span className="text-muted-foreground truncate">
                                {transaction.description || transaction.usage_type}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className={`font-medium ${transaction.amount > 0 ? 'text-success' : 'text-destructive'}`}>
                                {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(transaction.created_at), { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                        ))}
                        {(!recentTransactions || recentTransactions.length === 0) && (
                          <p className="text-sm text-muted-foreground text-center py-2">No recent activity</p>
                        )}
                      </div>
                    </div>

                    {/* Usage Forecast */}
                    {creditWallet && (
                      <div className="mt-4 p-3 bg-accent rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-foreground">Estimated usage</span>
                          <span className="text-sm font-medium text-foreground">
                            ~{Math.floor(creditWallet.credits_balance)} bills remaining
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          Based on 1 credit per bill processing
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Inbox - Notifications, Messages, Reminders */}
          {currentWorkspace && smartHeader.enabled && (
            <div className="flex items-center space-x-1">
              {/* Notifications */}
              {inboxNotifications.enabled && (
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowNotifications(!showNotifications);
                      setShowMessages(false);
                      setShowReminders(false);
                    }}
                    className="relative p-2 hover:bg-accent/10"
                  >
                    <Bell className="h-5 w-5 text-muted-foreground" />
                    {notifications.unreadCount > 0 && (
                      <Badge className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center">
                        {notifications.unreadCount > 99 ? '99+' : notifications.unreadCount}
                      </Badge>
                    )}
                  </Button>
                  <InboxDropdown
                    type="notifications"
                    isOpen={showNotifications}
                    onClose={() => setShowNotifications(false)}
                  />
                </div>
              )}

              {/* Messages */}
              {inboxMessages.enabled && (
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowMessages(!showMessages);
                      setShowNotifications(false);
                      setShowReminders(false);
                    }}
                    className="relative p-2 hover:bg-accent/10"
                  >
                    <MessageSquare className="h-5 w-5 text-muted-foreground" />
                    {messages.unreadCount > 0 && (
                      <Badge className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center">
                        {messages.unreadCount > 99 ? '99+' : messages.unreadCount}
                      </Badge>
                    )}
                  </Button>
                  <InboxDropdown
                    type="messages"
                    isOpen={showMessages}
                    onClose={() => setShowMessages(false)}
                  />
                </div>
              )}

              {/* Reminders */}
              {inboxReminders.enabled && (
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowReminders(!showReminders);
                      setShowNotifications(false);
                      setShowMessages(false);
                    }}
                    className="relative p-2 hover:bg-accent/10"
                  >
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    {reminders.overdueCount > 0 && (
                      <Badge className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center">
                        {reminders.overdueCount}
                      </Badge>
                    )}
                  </Button>
                  <InboxDropdown
                    type="reminders"
                    isOpen={showReminders}
                    onClose={() => setShowReminders(false)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Profile Menu */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowProfile(!showProfile)}
              className="p-2"
            >
              <User className="h-5 w-5 text-muted-foreground" />
            </Button>

            {showProfile && (
              <div className="absolute top-full right-0 mt-2 w-56 bg-background border border-border rounded-lg shadow-lg z-50">
                <div className="p-2">
                  {/* Personal Settings */}
                  <div className="px-2 py-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Personal</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      navigate('/account/profile');
                      setShowProfile(false);
                    }}
                    className="w-full justify-start hover:bg-accent/10"
                  >
                    <User className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span className="text-foreground">Profile Settings</span>
                  </Button>
                  
                  {profileMenuFull.enabled && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigate('/account/security');
                          setShowProfile(false);
                        }}
                        className="w-full justify-start hover:bg-accent/10"
                      >
                        <Shield className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span className="text-foreground">Security & Privacy</span>
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigate('/account/password');
                          setShowProfile(false);
                        }}
                        className="w-full justify-start hover:bg-accent/10"
                      >
                        <Lock className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span className="text-foreground">Change Password</span>
                      </Button>
                    </>
                  )}

                  {/* Workspace Settings */}
                  {currentWorkspace && (
                    <>
                      <Separator className="my-2" />
                      <div className="px-2 py-1">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Workspace</p>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigate(`/w/${currentWorkspace.id}/dashboard/settings`);
                          setShowProfile(false);
                        }}
                        className="w-full justify-start hover:bg-accent/10"
                      >
                        <Settings className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span className="text-foreground">Workspace Settings</span>
                      </Button>

                      {profileMenuFull.enabled && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigate(`/w/${currentWorkspace.id}/dashboard/team`);
                              setShowProfile(false);
                            }}
                            className="w-full justify-start hover:bg-accent/10"
                          >
                            <Users className="h-4 w-4 mr-2 text-muted-foreground" />
                            <span className="text-foreground">Team Management</span>
                          </Button>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigate(`/w/${currentWorkspace.id}/dashboard/integrations`);
                              setShowProfile(false);
                            }}
                            className="w-full justify-start hover:bg-accent/10"
                          >
                            <Zap className="h-4 w-4 mr-2 text-muted-foreground" />
                            <span className="text-foreground">Integrations</span>
                          </Button>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigate(`/w/${currentWorkspace.id}/dashboard/api`);
                              setShowProfile(false);
                            }}
                            className="w-full justify-start hover:bg-accent/10"
                          >
                            <FileText className="h-4 w-4 mr-2 text-muted-foreground" />
                            <span className="text-foreground">API Access</span>
                          </Button>
                        </>
                      )}

                      <Separator className="my-2" />
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigate(`/w/${currentWorkspace.id}/dashboard/billing`);
                          setShowProfile(false);
                        }}
                        className="w-full justify-start hover:bg-accent/10"
                      >
                        <CreditCard className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span className="text-foreground">Billing & Usage</span>
                      </Button>

                      {profileMenuFull.enabled && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            window.open('/support', '_blank');
                            setShowProfile(false);
                          }}
                          className="w-full justify-start hover:bg-accent/10"
                        >
                          <HelpCircle className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span className="text-foreground">Help & Support</span>
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};